import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send } from "lucide-react";
import { API } from "@/config";

const SUGGESTIONS = ["What does TPA actually build?", "How does AI lead response work?", "What does a growth system cost?"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "I'm the Polemikoi AI. Ask me anything about our growth systems — or tell me what your business is trying to achieve." },
  ]);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  if (!sessionRef.current) {
    sessionRef.current = localStorage.getItem("tpa_chat_session") || crypto.randomUUID();
    localStorage.setItem("tpa_chat_session", sessionRef.current);
  }

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") break;
          try {
            const { delta } = JSON.parse(data);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I'm briefly unavailable — please use the contact form and we'll respond within one business day." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen(!open)}
        aria-label="Open AI chat"
        className="fixed bottom-6 right-6 z-[80] flex h-14 w-14 items-center justify-center border border-white/20 bg-black/70 text-white backdrop-blur-xl transition-colors duration-300 hover:border-crimson hover:bg-crimson"
      >
        {open ? <X size={20} /> : <MessageSquare size={20} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-6 z-[80] flex h-[520px] w-[calc(100vw-3rem)] max-w-[400px] flex-col border border-white/15 bg-black/85 backdrop-blur-2xl"
          >
            <div className="border-b border-white/10 px-5 py-4">
              <p className="font-display text-sm font-bold uppercase tracking-[0.15em] text-white">Polemikoi AI</p>
              <p className="mt-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <span className="h-1.5 w-1.5 animate-pulse bg-crimson" /> Online — replies instantly
              </p>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-white text-black"
                        : "border border-white/10 bg-white/5 text-zinc-200"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? <span className="inline-block h-4 w-2 animate-pulse bg-crimson" /> : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${s.slice(0, 20).toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => send(s)}
                      className="border border-white/15 px-3 py-2 text-left text-xs text-zinc-400 transition-colors duration-300 hover:border-white/40 hover:text-white"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 border-t border-white/10 p-4">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about growth systems…"
                className="flex-1 bg-transparent px-3 py-2 text-sm text-white outline-none placeholder:text-zinc-600"
              />
              <button
                data-testid="chat-send-button"
                onClick={() => send()}
                disabled={streaming || !input.trim()}
                aria-label="Send message"
                className="bg-white px-4 text-black transition-colors duration-300 hover:bg-crimson hover:text-white disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
